import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, api } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { 
  Car, LogOut, Plus, Search, Calendar, MapPin, User, 
  CheckCircle, Truck, Clock, Menu, X, ClipboardList, ArrowRight
} from "lucide-react";
import { DashboardSkeleton } from "../components/DashboardSkeleton";

const StaffDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [bookingsRes, driversRes] = await Promise.all([
        api.get("/bookings"),
        api.get("/users/drivers")
      ]);
      setBookings(bookingsRes.data);
      setDrivers(driversRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => { logout(); navigate("/login"); };

  const getStatusBadge = (status) => {
    const styles = { pending: "status-pending", confirmed: "status-confirmed", dispatched: "status-dispatched", in_progress: "status-in_progress", completed: "status-completed", cancelled: "status-cancelled" };
    return <Badge className={`${styles[status]} capitalize`}>{status.replace("_", " ")}</Badge>;
  };

  const getPaymentBadge = (status) => {
    const styles = { pending: "payment-pending", paid: "payment-paid", failed: "payment-failed" };
    return <Badge className={`${styles[status]} capitalize`}>{status}</Badge>;
  };

  const filteredBookings = bookings.filter(booking => {
    const matchesSearch = booking.booking_ref.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.passenger_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.pickup_location.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || booking.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleConfirmBooking = async (bookingId) => {
    try {
      await api.post(`/bookings/${bookingId}/confirm`);
      toast.success("Booking confirmed");
      fetchData();
    } catch (error) { toast.error("Failed to confirm booking"); }
  };

  const handleDispatchBooking = async (bookingId, driverId) => {
    try {
      await api.post(`/bookings/${bookingId}/dispatch`, { driver_id: driverId, send_to_calendar: false });
      toast.success("Booking dispatched to driver");
      fetchData();
    } catch (error) { toast.error("Failed to dispatch booking"); }
  };

  const pendingCount = bookings.filter(b => b.status === "pending").length;
  const confirmedCount = bookings.filter(b => b.status === "confirmed").length;
  const dispatchedCount = bookings.filter(b => b.status === "dispatched").length;
  const todayBookings = bookings.filter(b => new Date(b.pickup_datetime).toDateString() === new Date().toDateString()).length;

  if (loading) return <DashboardSkeleton role="staff" />;

  const navItems = [
    { id: "bookings", icon: ClipboardList, label: "All Bookings" },
    { id: "new", icon: Plus, label: "New Booking", link: "/booking/new" },
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="staff-dashboard">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-[#111114] border-b border-white/[0.06]">
        <div className="flex items-center justify-between p-4">
          <img src="/limo-logo.jpg" alt="Limo Near Me" className="h-8 w-auto" />
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="text-white/70">
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-[#111114]/98 backdrop-blur-lg pt-16">
          <nav className="p-4 space-y-1">
            {navItems.map(item => (
              item.link ? (
                <Link key={item.id} to={item.link} onClick={() => setMobileMenuOpen(false)} className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-white/50 hover:bg-white/[0.04]">
                  <item.icon className="h-5 w-5" />{item.label}
                </Link>
              ) : (
                <button key={item.id} onClick={() => setMobileMenuOpen(false)} className="w-full flex items-center gap-3 px-4 py-3 rounded-lg bg-[#C8A95B]/10 text-[#C8A95B]">
                  <item.icon className="h-5 w-5" />{item.label}
                </button>
              )
            ))}
            <hr className="border-white/[0.06] my-4" />
            <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-red-400 hover:bg-red-500/10">
              <LogOut className="h-5 w-5" />Logout
            </button>
          </nav>
        </div>
      )}

      <div className="flex">
        {/* Desktop Sidebar */}
        <aside className="hidden lg:flex flex-col w-60 min-h-screen bg-[#111114] border-r border-white/[0.06] fixed">
          <div className="p-6 pb-4">
            <img src="/limo-logo.jpg" alt="Limo Near Me" className="h-10 w-auto" />
            <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-[#C8A95B]/10 border border-[#C8A95B]/20">
              <span className="text-[#C8A95B] text-[10px] font-medium uppercase tracking-wider">Staff Portal</span>
            </div>
          </div>
          <nav className="flex-1 px-3 space-y-0.5">
            {navItems.map(item => (
              item.link ? (
                <Link key={item.id} to={item.link} className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm text-white/45 hover:bg-white/[0.04] hover:text-white/75 transition-all" data-testid={`nav-${item.id}`}>
                  <item.icon className="h-[18px] w-[18px]" />{item.label}
                </Link>
              ) : (
                <button key={item.id} className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm bg-[#C8A95B]/10 text-[#C8A95B] font-medium transition-all" data-testid={`nav-${item.id}`}>
                  <item.icon className="h-[18px] w-[18px]" />{item.label}
                  <div className="ml-auto w-1.5 h-1.5 rounded-full bg-[#C8A95B]" />
                </button>
              )
            ))}
          </nav>
          <div className="p-3 border-t border-white/[0.06]">
            <div className="flex items-center gap-3 px-3 py-2 mb-1 rounded-lg">
              <div className="w-8 h-8 rounded-full bg-[#C8A95B]/15 flex items-center justify-center flex-shrink-0">
                <User className="h-4 w-4 text-[#C8A95B]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white/80 truncate">{user?.name}</p>
                <p className="text-[11px] text-white/30 capitalize">{user?.role}</p>
              </div>
            </div>
            <button onClick={handleLogout} className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-white/30 hover:text-red-400 hover:bg-red-500/10 transition-colors text-sm" data-testid="logout-btn">
              <LogOut className="h-4 w-4" />Logout
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 lg:ml-60 pt-16 lg:pt-0">
          <div className="p-4 lg:p-8">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
              <div>
                <h1 className="text-2xl lg:text-3xl font-bold">Staff Dashboard</h1>
                <p className="text-muted-foreground text-sm">Manage bookings and dispatch drivers</p>
              </div>
              <Link to="/booking/new">
                <Button className="bg-[#C8A95B] text-black hover:bg-[#d4b96a] rounded-full font-semibold" data-testid="new-booking-btn">
                  <Plus className="h-4 w-4 mr-2" />New Booking
                </Button>
              </Link>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {[
                { icon: Clock, color: "text-yellow-500", bg: "bg-yellow-500/10", label: "Pending", value: pendingCount },
                { icon: CheckCircle, color: "text-blue-500", bg: "bg-blue-500/10", label: "Confirmed", value: confirmedCount },
                { icon: Truck, color: "text-purple-500", bg: "bg-purple-500/10", label: "Dispatched", value: dispatchedCount },
                { icon: Calendar, color: "text-[#C8A95B]", bg: "bg-[#C8A95B]/10", label: "Today", value: todayBookings },
              ].map((stat, i) => (
                <Card key={i} className="border border-black/[0.06]">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg ${stat.bg} flex items-center justify-center`}>
                        <stat.icon className={`h-5 w-5 ${stat.color}`} />
                      </div>
                      <div>
                        <p className="text-2xl font-bold">{stat.value}</p>
                        <p className="text-xs text-muted-foreground">{stat.label}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input placeholder="Search bookings..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" data-testid="search-input" />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-44" data-testid="status-filter">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="dispatched">Dispatched</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Table */}
            <Card className="border border-black/[0.06] overflow-hidden">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-black/[0.06] bg-muted/30">
                        <th className="text-left px-4 py-3 font-medium text-muted-foreground text-xs uppercase tracking-wider">Reference</th>
                        <th className="text-left px-4 py-3 font-medium text-muted-foreground text-xs uppercase tracking-wider">Passenger</th>
                        <th className="text-left px-4 py-3 font-medium text-muted-foreground text-xs uppercase tracking-wider hidden md:table-cell">Route</th>
                        <th className="text-left px-4 py-3 font-medium text-muted-foreground text-xs uppercase tracking-wider">Date/Time</th>
                        <th className="text-left px-4 py-3 font-medium text-muted-foreground text-xs uppercase tracking-wider hidden lg:table-cell">Vehicle</th>
                        <th className="text-left px-4 py-3 font-medium text-muted-foreground text-xs uppercase tracking-wider">Status</th>
                        <th className="text-left px-4 py-3 font-medium text-muted-foreground text-xs uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-black/[0.04]">
                      {filteredBookings.map(booking => (
                        <tr key={booking.id} className="hover:bg-muted/20 transition-colors cursor-pointer" onClick={() => navigate(`/booking/${booking.id}`)}>
                          <td className="px-4 py-3">
                            <span className="font-mono text-[#C8A95B] font-medium text-xs">{booking.booking_ref}</span>
                          </td>
                          <td className="px-4 py-3">
                            <p className="font-medium text-sm">{booking.passenger_name}</p>
                            <p className="text-xs text-muted-foreground">{booking.passenger_phone}</p>
                          </td>
                          <td className="px-4 py-3 hidden md:table-cell">
                            <div className="max-w-[200px]">
                              <div className="flex items-center gap-1.5">
                                <div className="w-1.5 h-1.5 rounded-full bg-green-500 flex-shrink-0" />
                                <p className="text-sm truncate">{booking.pickup_location}</p>
                              </div>
                              <div className="flex items-center gap-1.5 mt-0.5">
                                <div className="w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0" />
                                <p className="text-xs text-muted-foreground truncate">{booking.dropoff_location}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <p className="text-sm">{new Date(booking.pickup_datetime).toLocaleDateString()}</p>
                            <p className="text-xs text-muted-foreground">{new Date(booking.pickup_datetime).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})}</p>
                          </td>
                          <td className="px-4 py-3 hidden lg:table-cell">
                            <span className="capitalize text-sm">{booking.vehicle_type?.replace(/_/g, ' ')}</span>
                          </td>
                          <td className="px-4 py-3">{getStatusBadge(booking.status)}</td>
                          <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                            <div className="flex items-center gap-2">
                              {booking.status === "pending" && (
                                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleConfirmBooking(booking.id)} data-testid={`confirm-${booking.id}`}>
                                  <CheckCircle className="h-3.5 w-3.5 mr-1" />Confirm
                                </Button>
                              )}
                              {(booking.status === "confirmed" || booking.status === "pending") && !booking.driver_id && drivers.length > 0 && (
                                <Select onValueChange={(driverId) => handleDispatchBooking(booking.id, driverId)}>
                                  <SelectTrigger className="w-28 h-7 text-xs" data-testid={`dispatch-${booking.id}`}>
                                    <SelectValue placeholder="Dispatch" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {drivers.map(d => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                              )}
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => navigate(`/booking/${booking.id}`)}>
                                <ArrowRight className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filteredBookings.length === 0 && (
                    <div className="text-center py-16 text-muted-foreground">
                      <ClipboardList className="h-10 w-10 mx-auto mb-3 opacity-30" />
                      <p>No bookings found</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    </div>
  );
};

export default StaffDashboard;
